/**
 * Pure simulation rules, ported from game/workers/data-generator.worker.js
 * and the main-thread fallback in live-data-manager.js. Runs inside the
 * module worker (dataGenerator.worker.ts) or on the main thread when workers
 * are unavailable, and is unit-tested directly.
 *
 * Rules: parse scientific notation and thousand separators; an id containing
 * error / count / packet / bits / corrected / uncorrectable, or a value with
 * no decimal point, stays an integer; negative values survive for dB and dBm
 * (and for values that start negative); a random walk of +/- variationPercent
 * of the current value; failure effects per active scenario stage; status
 * from normalRange (inside green, within a 20 % or 40 % margin yellow, beyond
 * red; no range means green).
 *
 * Two audited defects of the original worker are fixed here: the static
 * counter fields (maintenance_hours, total_hours, operating_hours,
 * service_hours) are skipped, and a key the main thread lists as holding a
 * live reading is never generated.
 */
import type { StatusColour } from '../types';

export interface GeneratorRange {
  low?: number;
  high?: number;
  min?: number;
  max?: number;
}

export interface GeneratorDataItem {
  id: string;
  label?: string;
  value: string | number;
  status?: string;
  normalRange?: GeneratorRange;
}

export interface GeneratorSubsystem {
  id: string;
  label?: string;
  data?: GeneratorDataItem[];
  childs?: GeneratorSubsystem[];
  subsystems?: GeneratorSubsystem[];
}

export interface GeneratorElement {
  id?: string;
  subsystems?: GeneratorSubsystem[];
  childs?: GeneratorSubsystem[];
}

/** One stage of a failure scenario: the numeric effects the generator applies (constants.js FAILURE_SCENARIOS). */
export interface FailureStageEffects {
  fanSpeedDrop?: number;
  tempRise?: number;
  currentRise?: number;
  vibrationRise?: number;
  powerDrop?: number;
  efficiencyDrop?: number;
  noiseFigureRise?: number;
  gainDrop?: number;
  berSpike?: number;
  ebN0Drop?: number;
}

/** Active effects keyed by scenario id. */
export type FailureEffectsMap = Record<string, FailureStageEffects>;

export interface GeneratedUpdate {
  subsystemId: string;
  dataId: string;
  newValue: string;
  numericValue: number;
  status: StatusColour;
}

export interface GeneratorBatch {
  updates: GeneratedUpdate[];
  timestamp: string;
}

export interface GeneratorContext {
  variationPercent: number;
  failureEffects: FailureEffectsMap;
  /** `${subsystemId}.${dataId}` keys the main thread says hold a live reading. */
  excludedKeys: ReadonlySet<string>;
  random: () => number;
  now?: () => number;
}

export const DEFAULT_VARIATION_PERCENT = 0.02;
export const EXCLUDED_LIVE_FIELDS = ['maintenance_hours', 'total_hours', 'operating_hours', 'service_hours'];
export const INTEGER_ID_KEYWORDS = ['error', 'count', 'packet', 'bits', 'corrected', 'uncorrectable'];
/** Temperature rise at a 100 % fan-speed drop (35 * drop^1.5). */
export const FAN_MAX_TEMP_RISE = 35;

/** Same shape as the store's readingKey, restated so the worker bundle does not import the store. */
export function simKey(subsystemId: string, dataId: string): string {
  return `${subsystemId}.${dataId}`;
}

/** Static counters the simulator must not vary (substring match, as the original main thread did). */
export function isExcludedField(dataId: string): boolean {
  const id = dataId.toLowerCase();
  return EXCLUDED_LIVE_FIELDS.some((f) => id.includes(f));
}

export interface ParsedValue {
  value: number;
  unit: string;
  scientific: boolean;
}

/** The number and unit in a shipped value string ("23.4 °C", "1,700 RPM", "1.2e-9"), or null when it has none. */
export function parseValue(raw: string | number): ParsedValue | null {
  const valueStr = String(raw);
  const scientific = /[0-9]e[+-]?[0-9]/i.test(valueStr);
  let value: number;
  let unit: string;
  if (scientific) {
    const sciMatch = valueStr.match(/([+-]?[0-9]*\.?[0-9]+[eE][+-]?[0-9]+)/);
    if (!sciMatch) return null;
    value = parseFloat(sciMatch[1]);
    unit = valueStr.replace(sciMatch[1], '').trim();
  } else {
    // One leading number with an optional unit. Text that merely contains digits
    // ('4 of 8', '24/24 UP', 'LOCKED on JASON-3') is not a reading and is left alone;
    // the old Live Mode mashed those digits together and varied the result.
    const m = valueStr.trim().match(/^([+-]?(?:\d{1,3}(?:,\d{3})+|\d+)?(?:\.\d+)?)\s*(.*)$/s);
    if (!m || m[1] === '' || m[1] === '-' || m[1] === '+') return null;
    if (/\d/.test(m[2])) return null;
    value = parseFloat(m[1].replace(/,/g, ''));
    unit = m[2].trim();
  }
  if (Number.isNaN(value)) return null;
  return { value, unit, scientific };
}

export function isIntegerField(dataId: string, raw: string | number): boolean {
  const idLower = dataId.toLowerCase();
  const valueStr = String(raw);
  return (
    INTEGER_ID_KEYWORDS.some((k) => idLower.includes(k)) || (!valueStr.includes('.') && !/e/i.test(valueStr))
  );
}

export function applyFailureEffects(sensorId: string, value: number, effects: FailureEffectsMap): number {
  const idLower = sensorId.toLowerCase();
  for (const stage of Object.values(effects)) {
    if (!stage) continue;
    // Temperature: a fan-speed drop heats non-linearly (35 * drop^1.5); other scenarios add their tempRise.
    if (idLower.includes('temperature') || idLower.includes('temp')) {
      if (stage.fanSpeedDrop) value += FAN_MAX_TEMP_RISE * Math.pow(stage.fanSpeedDrop, 1.5);
      else if (stage.tempRise) value += stage.tempRise;
    }
    if (
      stage.fanSpeedDrop &&
      (idLower.includes('fan') || idLower.includes('rpm') || idLower.includes('speed'))
    )
      value *= 1 - stage.fanSpeedDrop;
    if (stage.currentRise && idLower.includes('current')) value += stage.currentRise;
    if (stage.vibrationRise && idLower.includes('vibration')) value += stage.vibrationRise;
    if (stage.powerDrop && (idLower.includes('power') || idLower.includes('output')))
      value *= 1 - stage.powerDrop;
    if (stage.efficiencyDrop && idLower.includes('efficiency')) value -= stage.efficiencyDrop;
    if (stage.noiseFigureRise && idLower.includes('noise')) value += stage.noiseFigureRise;
    if (stage.gainDrop && idLower.includes('gain')) value -= stage.gainDrop;
    if (stage.berSpike && (idLower.includes('ber') || idLower.includes('error')))
      value = Math.max(value, stage.berSpike);
    if (stage.ebN0Drop && idLower.includes('eb')) value -= stage.ebN0Drop;
  }
  return value;
}

export function formatValue(value: number, unit: string, isInteger: boolean, isScientific: boolean): string {
  let formatted: string;
  if (isScientific || (Math.abs(value) > 0 && Math.abs(value) < 1e-4)) {
    formatted = value.toExponential(1);
    return unit ? formatted + ' ' + unit : formatted;
  }
  let precision = 1;
  if (isInteger) precision = 0;
  else if (Math.abs(value) < 0.01) precision = 4;
  else if (Math.abs(value) < 1) precision = 2;
  else if (Math.abs(value) >= 1000) precision = 0;
  formatted = value.toFixed(precision);
  if (Math.abs(value) >= 1000)
    formatted = Number(formatted).toLocaleString('en-US', { maximumFractionDigits: precision });
  return unit ? formatted + ' ' + unit : formatted;
}

/** Inside the range green; within a 20 % margin yellow; within a 40 % margin still yellow; beyond red. */
export function determineStatus(item: Pick<GeneratorDataItem, 'normalRange'>, value: number): StatusColour {
  const normal = item.normalRange;
  if (!normal) return 'green';
  const normalMin = normal.low !== undefined ? normal.low : normal.min;
  const normalMax = normal.high !== undefined ? normal.high : normal.max;
  if (normalMin === undefined || normalMax === undefined) return 'green';
  const margin = (normalMax - normalMin) * 0.2;
  if (value >= normalMin && value <= normalMax) return 'green';
  if (value >= normalMin - margin && value <= normalMax + margin) return 'yellow';
  if (value >= normalMin - margin * 2 && value <= normalMax + margin * 2) return 'yellow';
  return 'red';
}

/** One random-walk step for a data item; writes the new value back into the item (the generator's own copy) like the original. */
export function generateValueUpdate(
  subsystem: GeneratorSubsystem,
  item: GeneratorDataItem,
  ctx: GeneratorContext,
): GeneratedUpdate | null {
  if (isExcludedField(item.id)) return null;
  if (ctx.excludedKeys.has(simKey(subsystem.id, item.id))) return null;
  const parsed = parseValue(item.value);
  if (!parsed) return null;
  const integer = isIntegerField(item.id, item.value);

  let newValue = parsed.value + (ctx.random() - 0.5) * 2 * ctx.variationPercent * Math.abs(parsed.value);
  newValue = applyFailureEffects(item.id, newValue, ctx.failureEffects);

  const unitLower = parsed.unit.toLowerCase();
  const allowNegative = unitLower.includes('dbm') || unitLower.includes('db') || parsed.value < 0;
  if (!allowNegative) newValue = Math.max(0, newValue);
  if (integer) newValue = Math.round(newValue);

  const formatted = formatValue(newValue, parsed.unit, integer, parsed.scientific);
  const status = determineStatus(item, newValue);
  item.value = formatted;
  item.status = status;
  return { subsystemId: subsystem.id, dataId: item.id, newValue: formatted, numericValue: newValue, status };
}

function collect(
  subsystems: GeneratorSubsystem[] | undefined,
  ctx: GeneratorContext,
  updates: GeneratedUpdate[],
): void {
  if (!subsystems) return;
  for (const subsystem of subsystems) {
    for (const item of subsystem.data ?? []) {
      const update = generateValueUpdate(subsystem, item, ctx);
      if (update) updates.push(update);
    }
    collect(subsystem.subsystems, ctx, updates);
    collect(subsystem.childs, ctx, updates);
  }
}

/** One generation cycle over the whole element (root `subsystems` and legacy `childs`). */
export function generateBatch(element: GeneratorElement, ctx: GeneratorContext): GeneratorBatch {
  const updates: GeneratedUpdate[] = [];
  collect(element.subsystems, ctx, updates);
  collect(element.childs, ctx, updates);
  return { updates, timestamp: new Date(ctx.now ? ctx.now() : Date.now()).toISOString() };
}

// Worker protocol (dataGenerator.worker.ts).
export type WorkerInbound =
  | {
      type: 'start';
      payload: {
        element: GeneratorElement;
        interval: number;
        variationPercent?: number;
        failureEffects?: FailureEffectsMap;
        excludedKeys?: string[];
      };
    }
  | { type: 'stop' }
  | { type: 'setInterval'; payload: number }
  | { type: 'updateFailureEffects'; payload: FailureEffectsMap }
  | { type: 'setExcludedKeys'; payload: string[] };

export type WorkerOutbound = { type: 'batch'; payload: GeneratorBatch };
